/**
 * File: useCashFlowChartController.ts
 * Path: apps/admin/src/shared/hooks/useCashFlowChartController.ts
 * Description: Everything the cash-flow chart works out and remembers: the
 * scale, where each point sits, which column is shown and where the pointer is.
 */

import { useRef, useState, type MouseEvent } from "react";
import { niceAxis, type FlowPoint } from "../utils/overviewFiguresUtils";

const MAX_LABELS = 12;

export const useCashFlowChartController = (points: readonly FlowPoint[]) => {
  const [hover, setHover] = useState<number | null>(null);
  /** Where the pointer is inside the chart, as a fraction of its width and height; null for keyboard and touch. */
  const [pointer, setPointer] = useState<{ fx: number; fy: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const highest = points.reduce((m, p) => Math.max(m, p.inCents, p.outCents), 0);
  const axis = niceAxis(highest);
  const count = Math.max(points.length, 1);
  const x = (i: number) => ((i + 0.5) / count) * 100;
  const y = (cents: number) => 100 - (cents / axis.topCents) * 100;
  const line = (pick: (p: FlowPoint) => number) => points.map((p, i) => `${x(i)},${y(pick(p))}`).join(" ");
  const active = hover ?? Math.max(points.length - 1, 0);

  const onMouseMove = (e: MouseEvent) => {
    const rect = box.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      setPointer({ fx: (e.clientX - rect.left) / rect.width, fy: (e.clientY - rect.top) / rect.height });
    }
  };
  const onMouseLeave = () => {
    setHover(null);
    setPointer(null);
  };

  return {
    box,
    axis,
    count,
    x,
    y,
    line,
    active,
    shown: points[active],
    labelEvery: Math.ceil(count / MAX_LABELS),
    follow: pointer ?? { fx: x(active) / 100, fy: 0.04 },
    onMouseMove,
    onMouseLeave,
    columnHandlers: (i: number) => ({
      onMouseEnter: () => setHover(i),
      onFocus: () => setHover(i),
      onBlur: () => setHover(null),
      onClick: () => {
        setHover(i);
        setPointer(null);
      },
    }),
  };
};
